"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { HiExclamationTriangle } from "react-icons/hi2";
import { Button } from "@/components/ui";
import { useCurrentTime } from "@/hooks/use-current-time";
import { useTimezone } from "@/hooks/use-timezone";
import { formatClockedInDuration, getClockedInDurationMs } from "@/lib/check-in-duration-warning";
import { formatTime } from "@/lib/format-data";
import { toMs } from "@/lib/timestamp";
import { EventTiny } from "@/types/event";

// A clock-in/out happening this far outside the matching shift boundary is
// flagged as early/late.
const SHIFT_WARNING_THRESHOLD_MS = 30 * 60 * 1000;
export type ClockMode = "in" | "out";
function formatDuration(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} hr`;
  return `${hours} hr ${minutes} min`;
}

/**
 * Picks the shift the warning compares against. Clocking out anchors on the
 * clock-in time (the shift being ended) so a later shift isn't mistakenly
 * chosen; clocking in anchors on now. We return the shift containing the
 * anchor, otherwise the one whose start is closest to it.
 */
function pickRelevantShift(shifts: EventTiny[] | null | undefined, userId: string | undefined, mode: ClockMode, now: Date, clockedInAt?: string): EventTiny | undefined {
  const mine = shifts?.filter(shift => shift.attendees.some(attendee => attendee.user.id === userId));
  if (!mine?.length) return undefined;
  const anchorMs = mode === "out" && clockedInAt ? toMs(clockedInAt) : now.getTime();
  const containing = mine.find(shift => toMs(shift.start) <= anchorMs && toMs(shift.end) >= anchorMs);
  if (containing) return containing;
  return mine.reduce((closest, shift) => Math.abs(toMs(shift.start) - anchorMs) < Math.abs(toMs(closest.start) - anchorMs) ? shift : closest);
}
function getShiftWarning(mode: ClockMode, shift: EventTiny | undefined, now: Date, timezone: string): string | null {
  if (!shift) return null;
  const nowMs = now.getTime();
  if (mode === "in") {
    const startMs = toMs(shift.start);
    if (nowMs < startMs - SHIFT_WARNING_THRESHOLD_MS) {
      return `Your shift doesn't start until ${formatTime(shift.start, true, timezone)} — you're clocking in ${formatDuration(startMs - nowMs)} early.`;
    }
    if (nowMs > startMs + SHIFT_WARNING_THRESHOLD_MS) {
      return `Your shift started at ${formatTime(shift.start, true, timezone)} — you're clocking in ${formatDuration(nowMs - startMs)} late.`;
    }
    return null;
  }
  const endMs = toMs(shift.end);
  if (nowMs < endMs - SHIFT_WARNING_THRESHOLD_MS) {
    return `Your shift doesn't end until ${formatTime(shift.end, true, timezone)} — you're clocking out ${formatDuration(endMs - nowMs)} early.`;
  }
  if (nowMs > endMs + SHIFT_WARNING_THRESHOLD_MS) {
    return `Your shift ended at ${formatTime(shift.end, true, timezone)} — you're clocking out ${formatDuration(nowMs - endMs)} late.`;
  }
  return null;
}

/**
 * The confirmation body shown for clocking in/out — a time-aware greeting, the
 * live current time, a clock-out summary, and any shift warning. Shared by the
 * desktop popover and the mobile dialog.
 */
export function ClockInConfirmContent(t0) {
  const $ = _c(43);
  const {
    mode,
    todayShifts,
    clockedInAt,
    userId,
    locations,
    locationsLoading,
    onClockIn,
    onClockOut,
    onCancel
  } = t0;
  const timezone = useTimezone();
  const now = useCurrentTime(1000);
  let t1;
  if ($[0] !== now || $[1] !== timezone) {
    t1 = formatTime(now, true, timezone);
    $[0] = now;
    $[1] = timezone;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const nowLabel = t1;
  let t2;
  if ($[3] !== clockedInAt || $[4] !== mode || $[5] !== now || $[6] !== timezone || $[7] !== todayShifts || $[8] !== userId) {
    const shift = pickRelevantShift(todayShifts, userId, mode, now, clockedInAt);
    t2 = getShiftWarning(mode, shift, now, timezone);
    $[3] = clockedInAt;
    $[4] = mode;
    $[5] = now;
    $[6] = timezone;
    $[7] = todayShifts;
    $[8] = userId;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const warning = t2;
  let t3;
  if ($[10] !== clockedInAt || $[11] !== mode || $[12] !== now) {
    t3 = mode === "out" ? getClockedInDurationMs(clockedInAt, now) : null;
    $[10] = clockedInAt;
    $[11] = mode;
    $[12] = now;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const longClockInDurationMs = t3;
  let t4;
  if ($[14] !== mode) {
    t4 = mode === "out" && <p className="mb-1 text-body-emphasis text-foreground">Are you sure you want to clock out?</p>;
    $[14] = mode;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== nowLabel) {
    t5 = <p className="text-body text-foreground-secondary">It's {nowLabel} right now.</p>;
    $[16] = nowLabel;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t4 || $[19] !== t5) {
    t6 = <div>{t4}{t5}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== clockedInAt || $[22] !== mode || $[23] !== now || $[24] !== timezone) {
    t7 = mode === "out" && clockedInAt && <div className="flex flex-col gap-1 rounded-lg bg-muted/50 px-3 py-2 text-body"><div className="flex justify-between gap-4"><span className="text-foreground-secondary">Last clocked in</span><span className="font-medium text-foreground">{formatTime(clockedInAt, true, timezone)}</span></div><div className="flex justify-between gap-4"><span className="text-foreground-secondary">Time worked</span><span className="font-medium text-foreground">{formatDuration(now.getTime() - toMs(clockedInAt))}</span></div></div>;
    $[21] = clockedInAt;
    $[22] = mode;
    $[23] = now;
    $[24] = timezone;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== longClockInDurationMs) {
    t8 = longClockInDurationMs !== null && <div className="flex items-start gap-2 rounded-lg bg-status-error-bg px-3 py-2 text-caption text-status-error-text"><HiExclamationTriangle className="mt-0.5 size-4 shrink-0" /><span>Did you forget to clock out the day before? You've been clocked in for {formatClockedInDuration(longClockInDurationMs)}.</span></div>;
    $[26] = longClockInDurationMs;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== warning) {
    t9 = warning && <div className="flex items-start gap-2 rounded-lg bg-status-warning-bg px-3 py-2 text-caption text-status-warning-text"><HiExclamationTriangle className="mt-0.5 size-4 shrink-0" /><span>{warning}</span></div>;
    $[28] = warning;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== locations || $[31] !== locationsLoading || $[32] !== mode || $[33] !== onCancel || $[34] !== onClockIn || $[35] !== onClockOut) {
    t10 = mode === "in" ? locations === undefined ? <Button variant="primary" size="sm" className="w-full" onClick={() => onClockIn()}>Clock in</Button> : locations.length > 0 ? <div className="flex flex-col gap-2">{locations.map(location => <Button variant="secondary" key={location.id} size="sm" className="w-full" onClick={() => onClockIn(location.id)}>Clock in at {location.name}</Button>)}</div> : locationsLoading ? <Button variant="secondary" size="sm" className="w-full" isLoading={true} loadingText={"Loading locations\u2026"} /> : <p className="text-caption text-foreground-muted">No location available to clock in at.</p> : <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">{onCancel && <Button variant="secondary" size="sm" className="w-full sm:w-fit" onClick={onCancel}>Cancel</Button>}<Button size="sm" className="w-full sm:w-fit" variant="destructive" onClick={onClockOut}>Yes, clock out</Button></div>;
    $[30] = locations;
    $[31] = locationsLoading;
    $[32] = mode;
    $[33] = onCancel;
    $[34] = onClockIn;
    $[35] = onClockOut;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== t10 || $[38] !== t6 || $[39] !== t7 || $[40] !== t8 || $[41] !== t9) {
    t11 = <div className="flex flex-col gap-3">{t6}{t7}{t8}{t9}{t10}</div>;
    $[37] = t10;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  return t11;
}